import {z} from 'zod';
import {
	editRemovesFromFilter,
	getDefaultFilters,
	makeOptionalPropsNullable,
	type EditRemovesFromFilterParams,
	type GetDefaultFiltersParams,
} from '@netx/core-next/schema';
import {
	UploadAssetRequestSchema,
	type IUploadAssetRequest,
	UploadAssetRequestStatus,
	UploadAssetRequestField,
} from './uploadAssetRequestSchema';

export const UploadAssetRequestFilterSchema = makeOptionalPropsNullable(
	UploadAssetRequestSchema.partial().pick({
		[UploadAssetRequestField.categoryName]: true,
		[UploadAssetRequestField.creationDate]: true,
		[UploadAssetRequestField.creatorUserLabel]: true,
		[UploadAssetRequestField.expirationDate]: true,
		[UploadAssetRequestField.notifyLabel]: true,
		[UploadAssetRequestField.status]: true,
		[UploadAssetRequestField.title]: true,
	}),
).extend({
	// faux field to allow filtering on status by enabled/disabled
	enabled: z.boolean().nullable(),
});

export const UploadAssetRequestsFilterableField =
	UploadAssetRequestFilterSchema.keyof().enum;

export const UPLOAD_ASSET_REQUEST_FILTERABLE_FIELDS = Object.values(
	UploadAssetRequestsFilterableField,
);

export type UploadAssetRequestsFilter = z.infer<
	typeof UploadAssetRequestFilterSchema
>;

/** will return the filters passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getUploadAssetRequestsDefaultFilters = (
	params?: Omit<
		GetDefaultFiltersParams<UploadAssetRequestsFilter>,
		'filterableFields'
	>,
) =>
	getDefaultFilters({
		...params,
		filterableFields: UPLOAD_ASSET_REQUEST_FILTERABLE_FIELDS,
	});

/** given the original data, the new data, and the current filters, determine if the record should still be included in the filtered list */
export const uploadAssetRequestEditRemovesFromFilter = (
	params: Omit<
		EditRemovesFromFilterParams<IUploadAssetRequest, UploadAssetRequestsFilter>,
		'schema'
	>,
) => {
	const updateNeeded = editRemovesFromFilter({
		...params,
		schema: UploadAssetRequestSchema,
	});
	if (updateNeeded) return true;
	if (params.filters.enabled == null) return updateNeeded;
	// this is a faux filter field and does not actually directly match any props on the data object
	return params.filters.enabled === true
		? params.updatedData.status !== UploadAssetRequestStatus.ACTIVE
		: params.updatedData.status === UploadAssetRequestStatus.ACTIVE;
};
